import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Divider,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { dashboardService } from '../services/dashboardService';
import {
  dashboardChatService,
  DashboardChatMessage,
  DashboardChatUserOption,
} from '../services/dashboardChatService';
import { idoraforditasService, MunkaszamOsszesitoDto } from '../services/idoraforditasService';
import { ProjekttervezesFeladatSajat, ProjekttervezesFocus, projekttervezesService } from '../services/projekttervezesService';
import { authService } from '../services/authService';
import { formatDisplayDate, parseApiDate } from '../utils/workDays';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

interface DashboardProps {
  onAuthClick: () => void;
  onNavigateToIdoraforditas?: (munkaszam: string) => void;
  onNavigateToProjekttervezesFeladat?: (focus: ProjekttervezesFocus) => void;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

const normalizeMunkaszamDisplay = (value: string): string => {
  const raw = (value || '').trim().replace(/\s+/g, '');
  const match = raw.match(/^(\d{2,4})-(\d{1,3})\/(\d{1,})$/);

  if (!match) {
    return raw;
  }

  const year = Number(match[1]) % 100;
  const sorszam = Number(match[2]);
  const suffixRaw = match[3];
  const suffix = suffixRaw.length > 4 ? suffixRaw : Number(suffixRaw).toString().padStart(4, '0');

  return `${year.toString().padStart(2, '0')}-${sorszam.toString().padStart(3, '0')}/${suffix}`;
};

const buildTopMunkaszamChartData = (items: MunkaszamOsszesitoDto[]) =>
  items.map((item) => ({
    munkaszam: normalizeMunkaszamDisplay(item.munkaszam),
    munkaTargya: item.munkaTargya,
    datum: new Date(item.utolsoIdoraforditasDatum.toString()).toLocaleDateString('hu-HU'),
    osszesOraszam: item.osszesOraszam,
  }));

const getTopMunkaszamChartHeight = (itemCount: number) =>
  Math.max(260, itemCount * 34);

const chartContainerSx = {
  overflow: 'visible',
  '& .recharts-wrapper': {
    overflow: 'visible !important',
  },
  '& .recharts-tooltip-wrapper': {
    zIndex: 1500,
    pointerEvents: 'none',
  },
};

const chartCardSx = {
  height: '100%',
  width: '100%',
  overflow: 'visible',
  '& .MuiCardContent-root:last-child': {
    overflow: 'visible',
  },
};

const chartTooltipProps = {
  allowEscapeViewBox: { x: true, y: true } as const,
  wrapperStyle: { zIndex: 1500, pointerEvents: 'none' as const },
  contentStyle: { maxWidth: 360, whiteSpace: 'normal' as const },
};

const chartRowSx = {
  display: 'flex',
  gap: 2,
  mb: 3,
  flexWrap: 'wrap',
  alignItems: 'stretch',
  overflow: 'visible',
};

const formatChatMessageDate = (date: Date): string => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hour = date.getHours().toString().padStart(2, '0');
  const minute = date.getMinutes().toString().padStart(2, '0');
  return `${month}. ${day}. ${hour}:${minute}`;
};

const getDeadlineRowColor = (hatarido: string | null): string | undefined => {
  if (!hatarido) {
    return undefined;
  }

  const dueDate = parseApiDate(hatarido);
  if (!dueDate) {
    return undefined;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    return '#ffebee';
  }

  if (diffDays <= 3) {
    return '#fff3e0';
  }

  return undefined;
};

interface TopMunkaszamBarChartProps {
  title: string;
  data: MunkaszamOsszesitoDto[];
  barColor?: string;
  chartHeight?: number;
  xAxisTicks?: number[];
  forceTickGrid?: boolean;
}

const TopMunkaszamBarChart: React.FC<TopMunkaszamBarChartProps> = ({
  title,
  data,
  barColor = '#2e7d32',
  chartHeight,
  xAxisTicks,
  forceTickGrid = false,
}) => {
  const chartData = buildTopMunkaszamChartData(data);
  const resolvedChartHeight = chartHeight ?? getTopMunkaszamChartHeight(chartData.length);

  return (
    <Card sx={chartCardSx}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Box sx={chartContainerSx}>
          <ResponsiveContainer width="100%" height={resolvedChartHeight}>
            <BarChart layout="vertical" data={chartData} margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                ticks={xAxisTicks}
                domain={[0, xAxisTicks ? xAxisTicks[xAxisTicks.length - 1] : 'auto']}
                allowDecimals={false}
                interval={forceTickGrid ? 0 : undefined}
              />
              <YAxis
                type="category"
                dataKey="munkaszam"
                width={130}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                {...chartTooltipProps}
                formatter={(value: number) => [`${value.toFixed(2)} h`, 'Összes időráfordítás']}
                labelFormatter={(_, payload) => {
                  const item = payload && payload[0] ? payload[0].payload : null;
                  return item ? `${item.munkaTargya} - ${item.datum}` : '';
                }}
              />
              <Bar dataKey="osszesOraszam" fill={barColor} name="Összes időráfordítás" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard: React.FC<DashboardProps> = ({
  onAuthClick,
  onNavigateToIdoraforditas,
  onNavigateToProjekttervezesFeladat,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeProjects, setActiveProjects] = useState(0);
  const [totalMonthlyHours, setTotalMonthlyHours] = useState(0);
  const [currentUserMonthlyHours, setCurrentUserMonthlyHours] = useState(0);
  const [projectsByYear, setProjectsByYear] = useState<{ year: string; count: number }[]>([]);
  const [currentUserProjectDistribution, setCurrentUserProjectDistribution] = useState<{ projectName: string; munkaTargya: string; hours: number }[]>([]);
  const [utolsoMunkaszamok, setUtolsoMunkaszamok] = useState<MunkaszamOsszesitoDto[]>([]);
  const [topMunkaszamok, setTopMunkaszamok] = useState<MunkaszamOsszesitoDto[]>([]);
  const [topActiveMunkaszamok, setTopActiveMunkaszamok] = useState<MunkaszamOsszesitoDto[]>([]);
  const [feladataim, setFeladataim] = useState<ProjekttervezesFeladatSajat[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [chatMessages, setChatMessages] = useState<DashboardChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatDraft, setChatDraft] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState<DashboardChatUserOption[]>([]);
  const [mentionAnchorRange, setMentionAnchorRange] = useState<{ start: number; end: number } | null>(null);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [mentionQuery, setMentionQuery] = useState('');

  const currentYear = new Date().getFullYear().toString();
  const projectsThisYear = projectsByYear.find(item => item.year === currentYear)?.count ?? 0;
  const chartTileHeight = getTopMunkaszamChartHeight(10);
  const compactStatsTileHeight = 152;
  const compactStatsCardSx = {
    height: compactStatsTileHeight,
    minHeight: compactStatsTileHeight,
    border: '1px solid #e2e8f0',
    boxShadow: 'none',
  };
  const compactStatsLabelSx = {
    mt: 0.5,
    fontSize: '0.75rem',
    color: '#9aa4b2',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    const refreshInterval = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void loadWallMessages(true);
      }
    }, 3000);

    return () => {
      window.clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    const query = mentionQuery.trim();
    if (query.length < 1) {
      setMentionSuggestions([]);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        const users = await dashboardChatService.searchUsers(query, 12);
        setMentionSuggestions(users.slice(0, 8));
      } catch (err) {
        console.error('Hiba a felhasználó keresése során:', err);
        setMentionSuggestions([]);
      }
    }, 200);

    return () => window.clearTimeout(timeoutId);
  }, [mentionQuery]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch dashboard stats from backend
      const stats = await dashboardService.getDashboardStats();

      console.log('Dashboard stats loaded:', stats);

      // Set all stats
      setActiveProjects(stats.activeProjects);
      setTotalMonthlyHours(stats.totalMonthlyHours);
      setCurrentUserMonthlyHours(stats.currentUserMonthlyHours);
      setProjectsByYear(stats.projectsByYear);
      setCurrentUserProjectDistribution(stats.currentUserProjectDistribution);

      // Load dashboard tables
      await Promise.all([
        loadUtolsoMunkaszamok(),
        loadTopMunkaszamok(),
        loadTopActiveMunkaszamok(),
        loadFeladataim(),
        loadWallMessages(),
      ]);

    } catch (err) {
      console.error('Dashboard load error:', err);
      setError(`Hiba történt az adatok betöltése során: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const loadUtolsoMunkaszamok = async () => {
    try {
      const user = authService.getCurrentUser();
      if (user && user.userId) {
        const munkaszamok = await idoraforditasService.getUtolsoMunkaszamokByUserId(user.userId, 5);
        setUtolsoMunkaszamok(munkaszamok);
      }
    } catch (err) {
      console.error('Hiba az utolsó munkaszámok betöltése során:', err);
      // Ne dobjunk hibát, csak loggoljuk
    }
  };

  const loadTopMunkaszamok = async () => {
    try {
      const top10 = await idoraforditasService.getTopMunkaszamok(10);
      setTopMunkaszamok(top10);
    } catch (err) {
      console.error('Hiba a top munkaszámok betöltése során:', err);
    }
  };

  const loadTopActiveMunkaszamok = async () => {
    try {
      const top10 = await idoraforditasService.getTopActiveMunkaszamok(10);
      setTopActiveMunkaszamok(top10);
    } catch (err) {
      console.error('Hiba az aktív top munkaszámok betöltése során:', err);
    }
  };

  const loadFeladataim = async () => {
    try {
      const items = await projekttervezesService.getFeladataim();
      setFeladataim(items);
    } catch (err) {
      console.error('Hiba a Feladataim lista betöltése során:', err);
      setFeladataim([]);
    }
  };

  const handleMunkaszamClick = (munkaszam: string) => {
    if (onNavigateToIdoraforditas) {
      onNavigateToIdoraforditas(normalizeMunkaszamDisplay(munkaszam));
    }
  };

  const loadWallMessages = async (silent = false) => {
    if (!silent) {
      setChatLoading(true);
    }
    try {
      const messages = await dashboardChatService.getWallMessages(300);
      setChatMessages(messages);
    } catch (err) {
      console.error('Hiba a közös üzenőfal betöltése során:', err);
      setChatMessages([]);
    } finally {
      if (!silent) {
        setChatLoading(false);
      }
    }
  };

  const handleEditMessage = async (message: DashboardChatMessage) => {
    const updatedText = window.prompt('Üzenet szerkesztése:', message.messageText)?.trim();
    if (!updatedText || updatedText === message.messageText) {
      return;
    }

    try {
      const updated = await dashboardChatService.updateWallMessage(message.id, updatedText);
      setChatMessages((prev) => prev.map((item) => (item.id === message.id ? updated : item)));
      await loadWallMessages(true);
    } catch (err) {
      console.error('Hiba az üzenet szerkesztése során:', err);
    }
  };

  const handleDeleteMessage = async (message: DashboardChatMessage) => {
    const confirmed = window.confirm('Biztosan törlöd az üzenetet?');
    if (!confirmed) {
      return;
    }

    try {
      await dashboardChatService.deleteWallMessage(message.id);
      setChatMessages((prev) => prev.filter((item) => item.id !== message.id));
      await loadWallMessages(true);
    } catch (err) {
      console.error('Hiba az üzenet törlése során:', err);
    }
  };

  const updateMentionSuggestions = (text: string, caretPosition: number) => {
    const beforeCursor = text.slice(0, caretPosition);
    const atIndex = beforeCursor.lastIndexOf('@');
    if (atIndex < 0) {
      setMentionSuggestions([]);
      setMentionAnchorRange(null);
      setMentionQuery('');
      return;
    }

    const hasWhitespaceBefore = atIndex === 0 || /\s/.test(beforeCursor[atIndex - 1]);
    if (!hasWhitespaceBefore) {
      setMentionSuggestions([]);
      setMentionAnchorRange(null);
      setMentionQuery('');
      return;
    }

    const rawQuery = beforeCursor.slice(atIndex + 1);
    if (/\s/.test(rawQuery)) {
      setMentionSuggestions([]);
      setMentionAnchorRange(null);
      setMentionQuery('');
      return;
    }

    setMentionAnchorRange({ start: atIndex, end: caretPosition });
    setMentionQuery(rawQuery);
  };

  const handleMentionPick = (option: DashboardChatUserOption) => {
    if (!mentionAnchorRange) {
      return;
    }

    const before = chatDraft.slice(0, mentionAnchorRange.start);
    const after = chatDraft.slice(mentionAnchorRange.end);
    const nextText = `${before}@${option.displayName} ${after}`;
    setChatDraft(nextText);
    setMentionSuggestions([]);
    setMentionAnchorRange(null);
    setMentionQuery('');
  };

  const handleSendChatMessage = async () => {
    const trimmed = chatDraft.trim();
    if (!trimmed) {
      return;
    }

    try {
      const newMessage = await dashboardChatService.sendWallMessage(trimmed);
      setChatMessages((prev) => [...prev, newMessage]);
      setChatDraft('');
      setMentionSuggestions([]);
      setMentionAnchorRange(null);
      setMentionQuery('');
      await loadWallMessages(true);
    } catch (err) {
      console.error('Hiba az üzenet küldése során:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3, backgroundColor: '#f0f3f6' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Vezérlőpult
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gridTemplateRows: 'auto auto auto',
          gap: 2,
          mb: 3,
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ gridColumn: 1, gridRow: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5 }}>
            <Card sx={compactStatsCardSx}>
              <CardContent sx={{ py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <AssignmentIcon sx={{ fontSize: 28, color: '#0088FE' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.15 }}>
                      {activeProjects}
                    </Typography>
                    <Typography sx={compactStatsLabelSx}>
                      Aktív projektek
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 1.25 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TrendingUpIcon sx={{ fontSize: 28, color: '#00C49F' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.15 }}>
                      {projectsThisYear}
                    </Typography>
                    <Typography sx={compactStatsLabelSx}>
                      Projektek idén
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={compactStatsCardSx}>
              <CardContent sx={{ py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 28, color: '#FFBB28' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.15 }}>
                      {totalMonthlyHours.toFixed(1)}
                    </Typography>
                    <Typography sx={compactStatsLabelSx}>
                      Havi összes óra
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 1.25 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PeopleIcon sx={{ fontSize: 28, color: '#FF8042' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.15 }}>
                      {currentUserMonthlyHours.toFixed(1)}
                    </Typography>
                    <Typography sx={compactStatsLabelSx}>
                      Saját havi óráim
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={compactStatsCardSx}>
              <CardContent
                sx={{
                  p: '0 !important',
                  pb: '0 !important',
                  height: '100%',
                  '&:last-child': {
                    pb: '0 !important',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    px: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      ...chartContainerSx,
                      flex: '0 0 58%',
                      minWidth: 0,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <ResponsiveContainer width="100%" height={112}>
                      <PieChart>
                        <Pie
                          data={currentUserProjectDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={false}
                          outerRadius={44}
                          fill="#8884d8"
                          nameKey="projectName"
                          dataKey="hours"
                        >
                          {currentUserProjectDistribution.map((entry, index) => (
                            <Cell key={`tile-cell-${entry.projectName}-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          {...chartTooltipProps}
                          formatter={(value: number, _name: any, entry: any) => {
                            const total = currentUserProjectDistribution.reduce((sum, item) => sum + item.hours, 0);
                            const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                            const projectName = entry?.payload?.projectName || 'Munkaszám';
                            const munkaTargya = entry?.payload?.munkaTargya?.trim();
                            const tooltipName = munkaTargya && munkaTargya.length > 0 ? munkaTargya : projectName;
                            return [`${value.toFixed(2)}h (${percent}%)`, tooltipName];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box
                    sx={{
                      flex: '1 1 auto',
                      minWidth: 0,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                  <Typography
                    sx={{
                      ...compactStatsLabelSx,
                      mt: 0,
                      lineHeight: 1.35,
                      textAlign: 'right',
                      whiteSpace: 'normal',
                    }}
                  >
                    Saját munkaszám megoszlás
                  </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Box sx={{ gridColumn: 1, gridRow: 2, display: 'flex', overflow: 'visible' }}>
          <Card sx={chartCardSx}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Üzenőfal
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  border: '1px solid #d0d7de',
                  borderRadius: 2,
                  backgroundColor: '#f9fbfd',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: chartTileHeight,
                  height: chartTileHeight,
                  maxHeight: chartTileHeight,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderBottom: '1px solid #d0d7de',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Közös üzenőfal
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5 }}>
                  {chatLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <>
                      {chatMessages.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Még nincs üzenet.
                        </Typography>
                      ) : (
                        chatMessages.map((message) => {
                          const canManageMessage = message.isOwnMessage;
                          return (
                            <Box
                              key={message.id}
                              sx={{
                                mb: 1.5,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: message.isOwnMessage ? 'flex-end' : 'flex-start',
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                                {message.senderName} - {formatChatMessageDate(new Date(message.createdAt))}
                              </Typography>
                              <Box
                                sx={{ width: 'fit-content', maxWidth: '78%' }}
                                onMouseEnter={() => setHoveredMessageId(message.id)}
                                onMouseLeave={() => setHoveredMessageId((prev) => (prev === message.id ? null : prev))}
                              >
                                <Box
                                  sx={{
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 2,
                                    backgroundColor: message.isMentionedForCurrentUser ? '#fdeaea' : '#dff0ff',
                                    border: message.isMentionedForCurrentUser ? '1px solid #ef5350' : 'none',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  <Typography variant="body2">{message.messageText}</Typography>
                                </Box>
                                {canManageMessage && hoveredMessageId === message.id && (
                                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                    {message.isOwnMessage && (
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditMessage(message)}
                                        aria-label="Üzenet szerkesztése"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteMessage(message)}
                                      aria-label="Üzenet törlése"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    p: 1.5,
                    borderTop: '1px solid #d0d7de',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {mentionSuggestions.length > 0 && (
                    <Paper
                      variant="outlined"
                      sx={{
                        position: 'absolute',
                        left: 12,
                        right: 12,
                        bottom: 58,
                        zIndex: 10,
                        maxHeight: 180,
                        overflowY: 'auto',
                      }}
                    >
                      <List sx={{ p: 0 }}>
                        {mentionSuggestions.map((option) => (
                          <ListItemButton
                            key={option.id}
                            onClick={() => handleMentionPick(option)}
                          >
                            <ListItemText primary={option.displayName} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Paper>
                  )}
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Írj üzenetet..."
                    value={chatDraft}
                    onChange={(event) => {
                      const nextText = event.target.value;
                      setChatDraft(nextText);
                      updateMentionSuggestions(nextText, event.target.selectionStart ?? nextText.length);
                    }}
                    onClick={(event) => {
                      const target = event.target as HTMLInputElement;
                      updateMentionSuggestions(chatDraft, target.selectionStart ?? chatDraft.length);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        handleSendChatMessage();
                        return;
                      }
                      if (event.key === 'Escape') {
                        setMentionSuggestions([]);
                        setMentionAnchorRange(null);
                      }
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSendChatMessage}
                    disabled={chatDraft.trim().length === 0 || chatLoading}
                    aria-label="Üzenet küldése"
                  >
                    <SendIcon />
                  </IconButton>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ gridColumn: 1, gridRow: 3, display: 'flex', overflow: 'visible' }}>
          <TopMunkaszamBarChart
            title="Top 10 aktív projekt összes időráfordítás alapján"
            data={topActiveMunkaszamok}
            barColor="#1565c0"
            chartHeight={chartTileHeight}
            xAxisTicks={[0, 1000, 2000, 3000, 4000]}
            forceTickGrid
          />
        </Box>

        <Box sx={{ gridColumn: 2, gridRow: '1 / span 2', display: 'flex', overflow: 'visible' }}>
          <Card sx={chartCardSx}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Feladataim
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ flex: 1, minHeight: chartTileHeight + compactStatsTileHeight + 24, overflow: 'auto' }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Munkaszám</strong></TableCell>
                      <TableCell><strong>Munka tárgya</strong></TableCell>
                      <TableCell><strong>Feladat neve</strong></TableCell>
                      <TableCell><strong>Határideje</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feladataim.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            Nincs folyamatban lévő, hozzád rendelt feladat.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      feladataim.map((item) => {
                        const deadlineRowColor = getDeadlineRowColor(item.hatarido);
                        return (
                          <TableRow
                            key={`${item.tetelId}-${item.feladatId}`}
                            hover
                            onClick={() => {
                              if (onNavigateToProjekttervezesFeladat) {
                                onNavigateToProjekttervezesFeladat({
                                  tetelId: item.tetelId,
                                  feladatId: item.feladatId,
                                  csoportPath: item.csoportPath,
                                });
                              }
                            }}
                            sx={{
                              cursor: onNavigateToProjekttervezesFeladat ? 'pointer' : 'default',
                              backgroundColor: deadlineRowColor ?? 'inherit',
                              '&:hover': { backgroundColor: deadlineRowColor ?? 'action.hover' },
                            }}
                          >
                          <TableCell>
                            <Chip label={normalizeMunkaszamDisplay(item.munkaszam)} color="primary" size="small" />
                          </TableCell>
                          <TableCell sx={{ width: 280, maxWidth: 280 }}>
                            <Typography
                              variant="body2"
                              title={item.munkaTargya || '–'}
                              sx={{
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {item.munkaTargya || '–'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap title={item.feladatNev}>
                              {item.feladatNev}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatDisplayDate(parseApiDate(item.hatarido))}
                          </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ gridColumn: 2, gridRow: 3, display: 'flex', overflow: 'visible' }}>
          <TopMunkaszamBarChart
            title="Top 10 munkaszám összes időráfordítás alapján"
            data={topMunkaszamok}
            chartHeight={chartTileHeight}
          />
        </Box>
      </Box>

      {/* Floating Drawer az utolsó 5 munkaszámnak */}
      {utolsoMunkaszamok.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 240, // LeftPanel szélessége
            right: 0,
            zIndex: 1200,
            transition: 'transform 0.3s ease-in-out',
            transform: drawerOpen ? 'translateY(0)' : 'translateY(calc(100%))',
          }}
        >
          {/* Nyitó/Csukó gomb - keskeny pill a közepen */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              position: 'absolute',
              top: '-32px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1,
            }}
          >
            <IconButton
              size="small"
              onClick={() => setDrawerOpen(!drawerOpen)}
              sx={{ 
                backgroundColor: '#3a4651',
                color: 'white',
                padding: '4px 20px',
                borderRadius: '16px 16px 0 0',
                '&:hover': {
                  backgroundColor: '#4a5661',
                },
                minWidth: '60px',
              }}
            >
              {drawerOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowUpIcon fontSize="small" />}
            </IconButton>
          </Box>

          {/* Drawer tartalma */}
          <Box
            sx={{
              backgroundColor: 'white',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
              maxHeight: '300px',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Utoljára bejegyzett időráfordítások munkaszámok szerint
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Munkaszám</strong></TableCell>
                      <TableCell><strong>Munka tárgya</strong></TableCell>
                      <TableCell align="right"><strong>Összes óra</strong></TableCell>
                      <TableCell><strong>Utolsó dátum</strong></TableCell>
                      <TableCell><strong>Utolsó megjegyzés</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {utolsoMunkaszamok.map((item, index) => (
                      <TableRow
                        key={index}
                        hover
                        onClick={() => handleMunkaszamClick(item.munkaszam)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell>
                                <Chip label={normalizeMunkaszamDisplay(item.munkaszam)} color="primary" size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{
                              maxWidth: 260,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {item.munkaTargya}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {item.osszesOraszam.toFixed(2)} h
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(item.utolsoIdoraforditasDatum.toString()).toLocaleDateString('hu-HU')}
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {item.utolsoComment}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default Dashboard;
